import { PlayerEvents } from '@4tn/core-audio-player-shared';
import { isAbortError } from '@players/utils/error';
import EventDispatcher from '@utils/event-dispatcher';

class PlayerEventManager {
  private listeners: Record<PlayerEvents, [(...args: unknown[]) => void]> | object = {};

  public async dispacth(eventName: PlayerEvents, eventCallback: () => Promise<void>): Promise<void> {
    return await new Promise((resolve, reject) => {
      const promiseExecutor = async (): Promise<void> => {
        this.waitForEvent(eventName)
          .then(() => resolve())
          .catch(reject);
        await eventCallback().catch(reject);
      };
      void promiseExecutor();
    });
  }

  private waitForEvent<T extends { name: PlayerEvents }>(eventName: PlayerEvents): Promise<T> {
    return new Promise((resolve, reject) => {
      this.removeListener(eventName);
      this.removeListener(PlayerEvents.STREAM_ERROR);

      const handleEvent = (eventPayload: T): void => {
        resolve(eventPayload);
      };

      EventDispatcher.once(eventName, handleEvent);
      this.addListener(eventName, handleEvent);

      const handleStreamError = (error: any): void => {
        // Safari tends to throw abortion error when switching from stream to stream, which is not real error so we don't want to throw it
        if (isAbortError(error)) {
          return;
        }
        reject(error);
      };

      EventDispatcher.once(PlayerEvents.STREAM_ERROR, handleStreamError);
      this.addListener(PlayerEvents.STREAM_ERROR, handleStreamError);
    });
  }

  private addListener(listenerName: PlayerEvents, listenerCallback: (...args: unknown[]) => void): void {
    if (!this.listeners[listenerName]) {
      this.listeners[listenerName] = [listenerCallback];
    } else {
      this.listeners[listenerName].push(listenerCallback);
    }
  }

  private removeListener(eventToRemove: PlayerEvents): void {
    for (const key in this.listeners) {
      this.listeners[key].forEach((listener: (...args: unknown[]) => void) => {
        if (eventToRemove === key) {
          EventDispatcher.off(key, listener);
        }
      });
    }
    this.listeners[eventToRemove] = [];
  }
}

const playerEventManager = new PlayerEventManager();

const playerEventHandler = (eventName: PlayerEvents, eventCallback: () => Promise<void>): Promise<void> => {
  return playerEventManager.dispacth(eventName, eventCallback);
};

export default playerEventHandler;
